import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { multiSort, getNameOrEmailSlug } from 'utils'
/*
  VRG-491 — the SAME ring arithmetic the Analytics map's draw-to-filter uses, from the one
  module that owns it (§36). `prepareRing` reduces a raw gesture below the vertex cap,
  `ringsEqual` compares two rings on the wire, and `pointInRing` mirrors the server's SQL
  predicate so a shape drawn here selects the scans it would select there. It moved out of
  `store/analytics/` in this change precisely so this file could reach it without either
  importing another page's redux slice or writing a second copy of Douglas-Peucker.
*/
import { prepareRing, ringsEqual, pointInRing } from 'utils/geoRing'

import { useDidMountEffect, useFillBottom } from 'hooks'
import { useCertificateScans, isRenderResolve, renderResolveMeta } from 'hooks/api'

import { Box, Stack, Tooltip, useTheme } from '@mui/material'
import {
  CircleCheck,
  ShieldAlert,
  ArrowLeftRight,
  HelpCircle,
  CircleSlash,
  Route,
  Unlink,
} from 'lucide-react'

import {
  Button,
  getScanStatus,
  getScanTooltipPill,
  isInvalidScan,
  getScanCoords,
  type MapDrawMode,
  type MapItem,
  type MapRingPoint,
  type ScanStatusMeta,
} from '@repo/ui'
import { ActionLine } from 'components'
/*
  VRG-491 — the list | map split is the SAME component the Analytics page's "Scans by
  location" card renders (§36/§47). This tab used to hand-roll its own: a resizer built on
  raw `mousedown`/`mousemove` with no keyboard path and no `aria`, a percentage width, no
  collapse, no count strip, a flat coloured rectangle where §19 asks for a skeleton, and a
  remount `key` that tore Google Maps down and rebuilt it on every drag release. The
  Analytics card had already fixed every one of those — six separately-reported bugs' worth
  of "the map flashes and zooms" — and none of it reached here, because the two surfaces
  were two files drawing one picture.
*/
import { ScanMapPanel } from 'components/display/ScanMapPanel'
/*
  VRG-491 — draw-to-filter, the same control the Analytics map arms, from the same file
  (§36). What did NOT come with it is the Analytics wiring: that page commits the ring to
  page-global redux because every card on its canvas narrows to it, which is meaningless
  here — this tab is one list and one map, so the ring is component state a few lines
  below. The control is presentational for exactly that reason.
*/
import { MapDrawControl } from 'components/display/MapDrawControl'

import { ScanList } from './components/ScanList'

const ICON = { size: 14, strokeWidth: 2 } as const

// The ORTHOGONAL scan axes (per product): Scan Type (valid/invalid scan), Link status,
// and Certificate status (the cert's state at scan time). Each is its own StatusChip-style
// filter — value + label + hue + leading icon.
//
// ── THERE IS NO SEAL / "Tampered" AXIS HERE, and that is not an oversight (VRG-491) ──
// It existed until 2026-08-31 as `TAMPERED_OPTIONS` (Opened / Not tampered) and was
// removed because BOTH of its options matched zero rows, permanently — not rarely, not
// for now, but by construction:
//
//   This tab reads `GET /api/scan-log/public/:id`, which is backed by
//   `certificate_action_logs`. That table has NO `tampered` column, and 0 of 551 rows
//   carry a `tampered` key in metadata (measured 2026-08-31). Tamper data lives in the
//   separate `scans` table (3 true / 423 false / 6027 null), which is what feeds the
//   certificate HEADER's indicator and the analytics map — different surfaces, different
//   source. So `isTamperedScan()` on a row from THIS endpoint could only ever return
//   false, and the filter offered one bucket that caught everything and one that caught
//   nothing.
//
// Relabelling the buckets does not help; a bucket that cannot populate is not a wording
// problem. RESTORING the axis requires plumbing `scans.tampered` into
// `/scan-log/public/:id` FIRST — until that exists, re-adding these options ships two
// dead filters again. `sealState()` in `@repo/ui`'s `scanStatus.ts` is deliberately
// unwired and stays that way: it is the correct three-state resolver for the
// `scans`-backed surfaces, which is a different problem from this one.
const SCAN_TYPE_OPTIONS = [
  { value: 'valid', label: 'Valid', color: '#16A34A', icon: <CircleCheck {...ICON} /> },
  // Error red + shield-alert, matching the shared scanStatus resolver's `invalid` marker
  // hue. An earlier note justified the red as Invalid joining "the same severity tier as
  // Tampered" (VRG-155 / TD-061); that is no longer the resolver's position — Invalid is
  // severity 3 and Tampered is 0. The red here is Invalid's on its own account: a tap that
  // failed verification is the one scan outcome that warrants an alert.
  { value: 'invalid', label: 'Invalid', color: '#DC2626', icon: <ShieldAlert {...ICON} /> },
]
// VRG-106: render-token (`vt`) verification-failure EVENTS are their own orthogonal
// "Link status" axis — never bucketed into scan_type (see scanTypeValue below, which
// returns null for these rows so they're excluded from that axis' filter).
const LINK_STATUS_OPTIONS = [
  { value: 'link_suspicious', label: 'Suspicious link', color: '#DB2777', icon: <Unlink {...ICON} /> },
  { value: 'link_stale', label: 'Stale link', color: '#71717A', icon: <Unlink {...ICON} /> },
  { value: 'link_invalid', label: 'Invalid link', color: '#71717A', icon: <Unlink {...ICON} /> },
]
// Certificate-status buckets (collapse the raw enum into user-facing groups).
const CERT_STATUS_META: Record<string, { label: string; color: string; icon: React.ReactElement }> = {
  active: { label: 'Active', color: '#16A34A', icon: <CircleCheck {...ICON} /> },
  transfer: { label: 'In Transfer', color: '#7F48DF', icon: <ArrowLeftRight {...ICON} /> },
  lost: { label: 'Lost', color: '#D97706', icon: <HelpCircle {...ICON} /> },
  stolen: { label: 'Stolen', color: '#DC2626', icon: <ShieldAlert {...ICON} /> },
  inactive: { label: 'Inactive', color: '#71717A', icon: <CircleSlash {...ICON} /> },
}
// VRG-106: render-token EVENTS are excluded from the physical scan_type axis (null)
// so they never wrongly bucket as "valid" — they're their own "Link status" axis.
function scanTypeValue(scan: any): 'valid' | 'invalid' | null {
  if (isRenderResolve(scan)) return null
  return isInvalidScan(scan) ? 'invalid' : 'valid'
}
// VRG-106: the "Link status" filter axis value — null for physical scans.
function linkStatusValue(scan: any): string | null {
  return isRenderResolve(scan) ? renderResolveMeta(scan.render_reason).key : null
}
// Raw CertificateStatus enum -> the bucket used by the filter.
function certStatusBucket(scan: any): string | null {
  const s: string | undefined = scan.certificate_status ?? scan.snapshot?.status
  if (!s) return null
  if (s === 'active') return 'active'
  if (s === 'lost') return 'lost'
  if (s === 'stolen') return 'stolen'
  if (s.startsWith('transfer') || s === 'waiting_accept' || s === 'cancel_request') return 'transfer'
  return 'inactive' // created / inactive
}

const DEFAULT_PAGINATION = {
  limit: 50,
  offset: 0,
}

function scanLocationText(scan: any): string {
  if (scan.geo_city) return scan.geo_city
  // Same shared extractor the map uses, so a scan can never be searchable by a
  // coordinate the map refuses to plot (or vice versa) — this was a third copy of the
  // chain, and it had the same nested-`metadata.location` blind spot.
  const coords = getScanCoords(scan)
  return coords ? `${coords.lat}, ${coords.lng}` : ''
}
function escapeHtml(s: unknown): string {
  // Defensive: callers can pass a non-string — e.g. `metadata.location` is stored as a
  // { latitude, longitude } object — so coerce to avoid crashing the page with
  // "t.replace is not a function". (VRG-158.)
  return String(s ?? '').replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string,
  )
}
// Build the map hover popover as a compact "user cell" (avatar + name + location), mirroring
// the list row, plus a status pill for scans that earn one (verified stays unbadged — the
// expected state, where a "Valid" label would be noise). Dynamic values are escaped (the Map
// renders this as HTML).
//
// VRG-491 — the pill's label and tones come from the shared resolver via
// `getScanTooltipPill`, replacing a local `TOOLTIP_STATUS_PILL` table. That table read
// `tampered: { label: 'Tampered', <error red> }`, justified by a comment claiming Tampered
// and Invalid were "the same severity tier per the shared scanStatus resolver
// (VRG-155 / TD-061)". THAT JUSTIFICATION WAS STALE, in both halves: the resolver gives
// `tampered` severity 0 (equal to verified, deliberately BELOW invalid's 3) and a neutral
// zinc ramp, because the hardware reports only that a seal circuit opened — it cannot say
// who opened it or why, and the ordinary cause is the owner opening their own item. Only
// invalid scans raise an alert. (This paragraph used to close by noting that the file
// contradicted itself, since its own `TAMPERED_OPTIONS` filter already read "Opened" in
// neutral. That filter is gone as of VRG-491 — see the note above SCAN_TYPE_OPTIONS — so
// the corroboration is removed rather than left pointing at a deleted symbol. The resolver
// argument above stands on its own and is unaffected.)
function buildScanTooltip(
  name: string,
  location: string,
  status: ScanStatusMeta,
  avatar?: string,
): string {
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase()
  // Anonymous scan (no avatar, no real name) → generic person icon in a circle, matching
  // the row + leads treatment — NOT an "A" initial.
  const isAnon = !avatar && (!name || name.trim() === 'Anonymous' || !initials)
  const personIconSvg =
    `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style="opacity:0.7;"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>`
  const avatarHtml = avatar
    ? `<img src="${escapeHtml(avatar)}" style="width:32px;height:32px;border-radius:50%;object-fit:cover;flex:none;" />`
    : `<div class="vmt-avatar" style="width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;flex:none;">${isAnon ? personIconSvg : escapeHtml(initials)}</div>`
  const pill = getScanTooltipPill(status)
  const pillHtml = pill
    ? `<span style="margin-left:8px;padding:1px 8px;border-radius:999px;font-size:11px;font-weight:600;line-height:1.5;color:${pill.text};background:${pill.fill};border:1px solid ${pill.border};">${pill.label}</span>`
    : ''
  return (
    `<div style="display:flex;align-items:center;gap:11px;">${avatarHtml}` +
    `<div style="display:flex;flex-direction:column;line-height:1.35;">` +
    `<span style="display:flex;align-items:center;"><span class="vmt-name" style="font-size:14px;font-weight:600;">${escapeHtml(name)}</span>${pillHtml}</span>` +
    // Only show the location line when one was actually recorded (no "hidden" filler).
    (location
      ? `<span class="vmt-sub" style="font-size:13px;font-weight:400;">${escapeHtml(location)}</span>`
      : '') +
    `</div></div>`
  )
}

// Canonical location string for a scan — used for both the Location filter options
// and matching, so the option list and the filter comparison always agree.
function scanLocationValue(scan: any): string {
  // `metadata.location` is a raw { latitude, longitude } object on scans that submitted
  // location — never render it directly. Prefer a string form; otherwise derive from the
  // geocoded fields (metadata.city / lat,lng) via scanLocationText. (VRG-158.)
  const loc = scan.metadata?.location
  if (typeof loc === 'string' && loc) return loc
  return scanLocationText(scan)
}

export const Scans = () => {
  const { id } = useParams()
  const theme = useTheme()

  const [activeId, setActiveId] = useState<string | undefined>()
  const [pagination] = useState(DEFAULT_PAGINATION)
  const [search, setSearch] = useState('')
  // Default: newest scans first (always). Date sort defaults to desc app-wide too (Sort).
  // Empty = no user-set sort (Sort button shows no badge). Newest-first is applied as
  // the implicit default in visibleScans below, not as an active sort on the control.
  const [sort, setSort] = useState<Array<{ field: string; sort: string }>>([])
  // Applied filter model from the Filters drawer: [{ field, operator, value }].
  //
  // VRG-491: seeded from `?scan_type=invalid`, which is where the invalid-scan indicator in
  // the page header sends you — the count is only useful if it leads to the rows it counted.
  // The filter is a real entry in the SAME model the Filters drawer writes, deliberately, so
  // it shows up in the drawer as an applied filter and can be cleared like any other. A
  // separate "arrived via the header" display mode would be a second, invisible filtering
  // path with its own clear affordance to invent.
  //
  // Read once, as lazy initial state rather than an effect, so a later edit in the drawer is
  // never stomped by the URL the user arrived on.
  const [searchParams] = useSearchParams()
  const [filters, setFilters] = useState<Array<{ field: string; operator: string; value: any }>>(
    () =>
      searchParams.get('scan_type') === 'invalid'
        ? [{ field: 'scan_type', operator: 'isAnyOf', value: ['invalid'] }]
        : [],
  )
  /*
    The list | map split's own state — its width, whether the list is collapsed, and the
    map's re-layout signalling — now lives in `ScanMapPanel`, along with the hand-rolled
    `mousedown`/`mousemove` resizer this file used to carry. What is left here is what is
    genuinely this tab's: which scans to show, and what a row says.
  */
  // VRG-451: journey line off by default — it's an extra read of the same data, and
  // lines clutter fast when scans are many or scattered.
  const [showJourney, setShowJourney] = useState(false)

  /*
    ── DRAW-TO-FILTER (VRG-491) ──────────────────────────────────────────────────────
    Two pieces of state, and the split matters: `drawMode` is EPHEMERAL — which tool is
    armed — while `drawnRing` is the committed FILTER. Arming a lasso narrows nothing;
    finishing the stroke does.

    Both are LOCAL, and that is the whole reason this tab could not simply reuse the
    Analytics card's wiring. There, the ring goes to `setAnalyticsFilters({ area })` and
    into the request, because every card on that page narrows to one area and the server
    evaluates the predicate over rows the client never fetched. Here there is one list and
    one map over ONE certificate's scans, already loaded in full and already filtered on
    the client for search, status and sort — so the ring is one more client-side predicate
    among several (see `pointInRing`'s own header for that argument in full). Dispatching
    into analytics redux from a certificate page would be scoping a certificate's map to a
    slice that belongs to a different page, and would leak back out of this tab.
  */
  const [drawMode, setDrawMode] = useState<MapDrawMode>('off')
  const [drawnRing, setDrawnRing] = useState<MapRingPoint[] | null>(null)

  const handleDrawComplete = useCallback(
    (raw: MapRingPoint[]) => {
      // Disarm first, unconditionally — the stroke is over whatever the geometry turned
      // out to be, and leaving the tool armed after a rejected gesture is a control
      // silently disagreeing with the cursor.
      setDrawMode('off')
      /*
        A freehand loop emits several hundred vertices; `prepareRing` reduces it below the
        50-vertex cap and returns null if it cannot (see its header for why null rather
        than a truncated ring — a truncated polygon is a DIFFERENT area, silently). The
        reduced ring is what is stored, so the ring that filters and the ring drawn back
        onto the map are the same object.
      */
      const ring = prepareRing(raw)
      if (!ring) return
      // A redraw landing on the same shape must not replace the object: `visibleScans`
      // and `mapItems` key off it, and an equal-but-new array would recompute both and
      // clear the row selection for no visible reason.
      if (ringsEqual(ring, drawnRing)) return
      setDrawnRing(ring)
    },
    [drawnRing],
  )

  // Cancelling an in-progress stroke disarms the tool and leaves any COMMITTED ring
  // alone — abandoning a new gesture is not a request to clear the old filter.
  const handleDrawCancel = useCallback(() => setDrawMode('off'), [])

  // VRG-106 follow-up #2: render-token (`vt`) verification-failure EVENTS are merged
  // server-side into this same action-log-backed response (GetPublicScanLogsUseCase),
  // so the surface needs only ONE fetch — no separate /records round-trip.
  const { data, refetch, isLoading } = useCertificateScans({
    certificateId: id as string,
    ...pagination,
  })
  const scans = useMemo(() => data?.data || [], [data])

  useDidMountEffect(() => {
    refetch()
  }, [])

  /**
   * FIRST LOAD ONLY (§19) — the same latch the Analytics card documents at length.
   *
   * `useQuerySelect` sets `isLoading = true` on EVERY params change, so gating the split on
   * it alone would replace the whole tab — list AND map — with skeletons on each refetch,
   * and unmounting the split destroys the Google Map, which tiles for its MOUNT-TIME box.
   * "Has this surface ever loaded" is a property of the surface's lifetime, not of the
   * current request, so it latches once and never goes back. A certificate with no scans
   * still gets its empty state: the first response is a real object, so it latches on
   * arrival.
   */
  const [hasEverLoaded, setHasEverLoaded] = useState(false)
  useEffect(() => {
    if (data && !hasEverLoaded) setHasEverLoaded(true)
  }, [data, hasEverLoaded])
  const showSkeleton = isLoading && !hasEverLoaded

  // Client-side search + status filter + sort. The scan list is small (a single
  // certificate's scans), so filtering/sorting on the client keeps the top-bar
  // controls instant without extra round-trips.
  //
  // VRG-491 — this is the set BEFORE the drawn area is applied, and it is kept separate
  // for one reason: the empty state has to be able to say which of the two emptied the
  // view. "No scans match these filters" points at the ActionLine; "No scans in the drawn
  // area" points at the map. Collapsing them into one memo would leave the copy guessing,
  // and guessing wrong is how this surface came to claim a certificate had no scans while
  // rendering three of them.
  const filteredScans = useMemo(() => {
    let list = scans as any[]
    const q = search.trim().toLowerCase()
    if (q) {
      list = list.filter((s) =>
        [
          s.user?.first_name,
          s.user?.last_name,
          s.user?.email,
          scanLocationText(s),
          getScanStatus(s).label,
          s.type,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q),
      )
    }
    // Apply the Filters-drawer model. Three orthogonal status axes + location + date:
    //  scan_type          -> valid / invalid            (isAnyOf)
    //  link_status        -> suspicious / stale / invalid (isAnyOf)
    //  certificate_status -> active/transfer/lost/stolen (isAnyOf, bucketed)
    //  location           -> city string                 (isAnyOf)
    //  created_at         -> date range                  (onOrAfter / onOrBefore)
    for (const f of filters) {
      const val = f.value
      if (val == null || (Array.isArray(val) && val.length === 0)) continue
      if (f.field === 'scan_type')
        list = list.filter((s) => {
          const v = scanTypeValue(s)
          return v != null && (val as string[]).includes(v)
        })
      else if (f.field === 'link_status')
        list = list.filter((s) => {
          const v = linkStatusValue(s)
          return v != null && (val as string[]).includes(v)
        })
      else if (f.field === 'certificate_status')
        list = list.filter((s) => (val as string[]).includes(certStatusBucket(s) as string))
      else if (f.field === 'location')
        list = list.filter((s) => (val as string[]).includes(scanLocationValue(s)))
      else if (f.field === 'created_at') {
        const t = new Date(val).getTime()
        if (!Number.isNaN(t)) {
          if (f.operator === 'onOrAfter') list = list.filter((s) => new Date(s.created_at).getTime() >= t)
          else if (f.operator === 'onOrBefore')
            list = list.filter((s) => new Date(s.created_at).getTime() <= t)
        }
      }
    }
    // Derive sortable keys the multiSort can read directly off each row.
    // No `tampered` key here any more (VRG-491). It existed ONLY so `multiSort` could read
    // a sortable boolean for the `tampered` column, and that column is gone — `sort` is
    // local state fed exclusively by the Sort control, which is built from `columns`, so
    // nothing can put `tampered` in it. Nothing downstream reads `row.tampered` either
    // (checked: ScanList / ScanItem / ScanMap / Timeline all resolve status through
    // `getScanStatus`, never the decorated key), so this was genuinely orphaned rather
    // than merely unused-looking.
    const decorated = list.map((s) => ({
      ...s,
      status: getScanStatus(s).label,
      location: scanLocationText(s),
    }))
    const sortMap = sort.reduce<Record<string, string>>((acc, cur) => {
      acc[cur.field] = cur.sort
      return acc
    }, {})
    if (Object.keys(sortMap).length) return multiSort(decorated, sortMap)
    // Default order (no active sort on the button): newest scans first.
    return [...decorated].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
  }, [scans, search, filters, sort])

  /**
   * The drawn area, applied on top of everything above — VRG-491.
   *
   * ## It COMPOSES, it does not replace
   *
   * The ring is one more predicate ANDed onto the set the search box and the ActionLine's
   * status chips already produced, exactly as the analytics ring ANDs with that page's
   * `location` filter. They constrain different things — the chips read a scan's outcome,
   * the ring reads its coordinates — so there is no "does it replace or combine" question
   * to answer: drawing narrows what the chips left, and clearing the ring returns it.
   *
   * ## Why it filters the LIST and not just the map
   *
   * `mapItems` and the `ScanList` both derive from this one array, so a scan the ring
   * excludes disappears from the pins AND the rows in the same render. That is not a
   * convenience — a map and a list side by side that disagree about which scans exist is
   * the surface lying about its own filter, and the reader has no way to tell which half
   * is right.
   *
   * ## Unlocated scans drop out, deliberately
   *
   * A scan whose visitor declined the location prompt has no coordinates, so it cannot be
   * inside any region. Keeping it in the list would answer "which scans happened HERE"
   * with rows that cannot say where they happened. It comes back the moment the area is
   * cleared, and the count strip above the list moves with it, so nothing is hidden
   * silently.
   */
  const visibleScans = useMemo(() => {
    if (!drawnRing) return filteredScans
    return filteredScans.filter((scan: any) => {
      // The SAME extractor the pins use, not a `??` chain written here — a scan must
      // never be plottable by one code path and invisible to the other (see `mapItems`).
      const coords = getScanCoords(scan)
      return coords !== null && pointInRing(coords, drawnRing)
    })
  }, [filteredScans, drawnRing])

  // A selection is only meaningful against the rows it was made on. When the search, the
  // filters or the sort change, the visible set is a different set of scans, so a held id
  // would highlight nothing while still suppressing the "nothing selected" state.
  useEffect(() => {
    setActiveId(undefined)
  }, [visibleScans])

  const handleSortChange = (col: any, direction?: string, override?: boolean) => {
    if (Array.isArray(col)) return setSort(col)
    if (col === 'all' && direction === 'clear') return setSort([])
    setSort((prev) => {
      if (override) return [{ field: col, sort: direction as string }]
      const next = [...prev]
      const i = next.findIndex((s) => s.field === col)
      if (i !== -1) next[i].sort = direction as string
      else next.push({ field: col, sort: direction as string })
      return next
    })
  }

  /** Build MapItem[] with status-matched fillColor + a hover title. */
  const mapItems: MapItem[] = useMemo(() => {
    const isDark = theme.palette.mode === 'dark'
    return visibleScans
      // Coordinates come from the SHARED extractor, not a `??` chain written here.
      // The chain that used to live at this line read four spellings and missed the
      // fifth — `metadata.location` as an OBJECT, which is what the public-page
      // location submit actually writes — so a scan that had been reverse-geocoded to
      // "Miami, Florida, United States" rendered in the list beside a map insisting no
      // scans existed. See `getScanCoords` for the full account. (§28/§36.)
      .map((scan: any) => ({ scan, coords: getScanCoords(scan) }))
      .filter((entry): entry is { scan: any; coords: { lat: number; lng: number } } =>
        entry.coords !== null,
      )
      .map(({ scan, coords }) => {
        const name = (getNameOrEmailSlug(scan.user) as string) || 'Anonymous'
        const location = scanLocationValue(scan) // VRG-158: string, never the raw location object
        // VRG-491 — ONE resolver call now drives the pin, the cluster and the tooltip.
        // `getScanStatus` already checks render-token events FIRST, then invalid, then
        // tampered, so the render-vs-physical split does not need restating here; the three
        // hand-rolled ladders this replaces had each drifted from it in a different way.
        const status = getScanStatus(scan, { isDark })
        // Cluster severity: a cluster inherits its most-alerting member's colour, so a
        // genuinely alarming scan stays visible even when stacked with others.
        //
        // ⚠️ These two fields were DEAD CODE until VRG-491, because this map passed
        // `enableClustering={false}` — the ladder below was computed and then thrown away.
        // Clustering is on now (the Analytics map has always had it), which is what makes
        // it real. The Analytics map had the mirror-image half of the same bug: clustering
        // on, neither field supplied, so every cluster rendered in the neutral default.
        //
        // `status.severity`, not the old `tampered ? 2 : invalid ? 1 : 0` ladder. That
        // ladder ranked an OPENED SEAL above a FAILED VERIFICATION, so a pin stacking one
        // of each spoke for the seal and buried the invalid scan underneath it — the exact
        // inversion the resolver's severity field exists to prevent (`tampered` is 0, equal
        // to verified, because a broken seal is the expected outcome of a closure tag being
        // opened; `invalid` is 3). It also read `renderMeta?.suspicious`, a property
        // `ScanStatusMeta` does not have, so a suspicious link silently ranked as an
        // ordinary one.
        const clusterPriority = status.severity
        // Only an outcome that outranks "routine" contributes a cluster accent. Passing one
        // at severity 0 would be inert anyway — the Map's cluster renderer requires
        // `priority > 0` before a member's colour can win.
        const clusterColor = status.severity > 0 ? status.color : undefined
        return {
          id: scan.id,
          lat: coords.lat,
          lng: coords.lng,
          fillColor: status.color,
          clusterPriority,
          clusterColor,
          // Hover popover = the user cell + a status pill for any outcome that earns one
          // (verified does not — absence is its signal).
          tooltipHtml: buildScanTooltip(name, location, status, scan.user?.avatar || undefined),
        }
      })
  }, [visibleScans, theme.palette.mode])

  // VRG-451 — journey path: the ORDER this certificate moved between scans. The list
  // beside the map already conveys sequence; the map did not, so a Milan -> NY -> Tokyo
  // trip read as three unrelated dots.
  //
  // Ordered by `created_at` ASCENDING, deliberately independent of `mapItems` (which
  // follows whatever sort/filter the user picked in the list). A journey has one true
  // order — time — and re-sorting the list must not redraw the route.
  //
  // Consecutive duplicate coordinates are collapsed: repeat scans in one city would
  // otherwise add zero-length legs that render as arrowhead clutter on a single point.
  const journeyPath = useMemo(() => {
    const located = visibleScans
      .filter((scan: any) => {
        const lat = scan.geo_lat ?? scan.lat ?? scan.metadata?.lat ?? scan.metadata?.latitude
        const lng = scan.geo_lng ?? scan.lng ?? scan.metadata?.long ?? scan.metadata?.longitude
        return lat != null && lng != null
      })
      .slice()
      .sort(
        (a: any, b: any) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      )
      .map((scan: any) => ({
        lat: Number(scan.geo_lat ?? scan.lat ?? scan.metadata?.lat ?? scan.metadata?.latitude),
        lng: Number(scan.geo_lng ?? scan.lng ?? scan.metadata?.long ?? scan.metadata?.longitude),
      }))

    return located.filter(
      (p, i) => i === 0 || p.lat !== located[i - 1].lat || p.lng !== located[i - 1].lng,
    )
  }, [visibleScans])

  // Only offer the toggle when there is an actual route to draw (≥2 distinct places).
  const canShowJourney = journeyPath.length >= 2

  /**
   * WHICH changes are allowed to re-frame the map — VRG-491.
   *
   * `fitToItems` alone re-frames on every item-set change, which was fine while the only
   * thing that could change the set was a filter meaning "show me a different slice".
   * Draw-to-filter breaks that: a ring means "narrow within the view I am looking at", so
   * fitting to the survivors yanks the camera out from under the shape the reader just
   * drew and re-centres on whatever is left. Observed on the first pass of this feature,
   * and it is the same bug the Analytics card was reported for twice.
   *
   * So the signature deliberately OMITS `drawnRing`. Everything in it is a change of
   * subject and should re-frame; the ring is a change of scope within the subject and
   * should not. `sort` is omitted for a duller reason — reordering the same points is not
   * a different place.
   *
   * `fitSignature` rather than `suppressFit`: the latter is a one-shot flag the consumer
   * arms on one render and the map consumes on a later one, and `MapProps` is explicit
   * that the two sides can disagree about which commit that is. A signature has nothing
   * to race — the map reads it and `items` in the same render.
   */
  const fitSignature = useMemo(
    () => JSON.stringify([id, scans.length, search, filters]),
    [id, scans.length, search, filters],
  )

  // The map's empty state has FOUR causes and they mean opposite things to a brand.
  // It used to say "No scans recorded yet" for all of them, which is a lie in three cases
  // out of four: the map goes empty whenever nothing is PLOTTABLE, and a scan is
  // unplottable whenever it has no coordinates — which is the normal case for a scan
  // whose visitor declined the location prompt, and for a link-open event, neither of
  // which is a failure. A brand reading "no scans recorded" beside a list of three
  // scans concludes that tracking is broken.
  //
  // So the message distinguishes "there is nothing" from "there is something the map
  // cannot place", and in the latter case says WHY, since the reason is the actionable
  // part: location is the visitor's to give.
  //
  // VRG-491 — a TITLE and a TEXT, matching the Analytics card, because there are now two
  // places to say it and they want different lengths. The map overlay takes the title
  // alone (a two-sentence overlay doubles its height for something the first clause has
  // already said); the list's empty state beside it takes both, which is where the
  // explanation belongs. Before this the list ignored the resolver entirely and asserted
  // "This certificate has no scans yet" even while rendering a filtered-to-nothing view of
  // scans that plainly existed.
  //
  // VRG-491 also added the FOURTH cause: a drawn area that contains nothing. It is a
  // FILTER case, not a no-scans case, and the ORDER of the two tests below is what keeps
  // it honest. `filteredScans` is read first, so a view already emptied by the search or
  // the status chips still names them \u2014 an area that could never have mattered is not
  // blamed for a set that was empty before it was drawn. Only when the chips left
  // something and the ring removed all of it does the copy point at the map, which is the
  // one case where "adjust the filters above" would send the reader to the wrong control:
  // the area is cleared ON the map, not in the ActionLine.
  const emptyCopy = useMemo(() => {
    if (scans.length === 0) {
      return {
        title: 'No scans recorded yet',
        text: 'Scans appear here once someone taps this certificate\u2019s tag.',
      }
    }
    if (filteredScans.length === 0) {
      return {
        title: 'No scans match these filters',
        text: 'Adjust the search or the filters above to see scans here.',
      }
    }
    if (visibleScans.length === 0) {
      return {
        title: 'No scans in the drawn area',
        text: 'Clear the area on the map, or draw a different one, to see scans here.',
      }
    }
    return visibleScans.length === 1
      ? {
          title: 'This scan has no location',
          text: 'Visitors choose whether to share one, so a scan can be recorded without a place to plot it.',
        }
      : {
          title: 'These scans have no location',
          text: 'Visitors choose whether to share one, so a scan can be recorded without a place to plot it.',
        }
  }, [scans.length, filteredScans.length, visibleScans.length])

  // VRG-161: always read the VITE_-prefixed var. The old env-conditional fell back to
  // `import.meta.env.GOOGLE_MAPS_API_KEY` (no VITE_ prefix) in non-dev builds, which Vite
  // never exposes to the client → "Google Maps API key not configured" on every deploy.
  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY

  // Fill the region below the ActionLine to 24px above the viewport bottom — the same
  // gap as the page's L/R gutter (§22) — then scroll the list internally.
  //
  // This is the SHARED `useFillBottom`, the same measurement the DataTable's
  // `fillBottom` runs, rather than the ten-line copy that used to sit here. That copy
  // listened only to `window.resize` and `scans.length`, so any reflow that moved this
  // pane's top without resizing the viewport (the skeleton→content swap, the
  // ActionLine's filter row appearing, tabs settling) left a stale height: too short
  // and the pane floated above a dead zone, too tall and it overflowed the viewport.
  // §22 is explicit that the per-page magic number is the thing to stop doing, so the
  // arithmetic was extracted rather than copied a third time (§28/§36).
  const { ref: bodyRef, height: bodyHeight } = useFillBottom<HTMLDivElement>({
    minHeight: 320,
    deps: [scans.length],
  })

  /*
    Selection-scroll sync and the resize gesture both live in `ScanMapPanel` now. The
    divider here was a `Box` with a `mousedown` listener and a floating grip pill: no
    keyboard path, no `role="separator"`, no `aria-valuenow`, and a drag-end that bumped a
    remount `key` so Google Maps was destroyed and rebuilt every time you let go of it. The
    shared panel uses `ResizableSplit` (keyboard-operable, correctly labelled) and re-lays
    out the LIVE map instead of rebuilding it.
  */


  // Columns drive BOTH the Sort popover (sortable) and the Filters drawer (a control
  // per field, by `type`). The three status axes are `statusChip` (icon + color toggles);
  // Certificate-status + Location options are derived from the loaded scans.
  const columns = useMemo(() => {
    // Certificate-status buckets actually present (fall back to the canonical set so the
    // filter is still usable when the data hasn't populated certificate_status yet).
    const presentCert = Object.keys(CERT_STATUS_META).filter((b) =>
      scans.some((s: any) => certStatusBucket(s) === b),
    )
    const certKeys = presentCert.length ? presentCert : ['active', 'transfer', 'lost', 'stolen']
    const certOptions = certKeys.map((b) => ({ value: b, ...CERT_STATUS_META[b] }))
    // Location options grouped Country -> City. value = the canonical location string used
    // for matching; label = the city; country = the group header. (Plain object, not a JS
    // Map — this file used to import the @repo/ui `Map` component, which shadowed the
    // global constructor; the plain object outlived it because it is what this actually is.)
    const locSeen: Record<
      string,
      { value: string; label: string; country: string; state?: string }
    > = {}
    for (const s of scans as any[]) {
      const value = scanLocationValue(s)
      if (!value || locSeen[value]) continue
      // Reads structured geo_* first, falling back to the metadata blob. State is
      // populated by the server-side reverse-geocode (VRG-100) when a scan's coords
      // arrive + GOOGLE_GEOCODING_API_KEY is set; it's undefined otherwise.
      const city = s.geo_city || s.metadata?.city || value.split(',')[0].trim()
      const country = s.geo_country || s.metadata?.country || 'Unknown'
      const state = s.geo_state || s.metadata?.state || undefined
      locSeen[value] = { value, label: city, country, state }
    }
    const locationOptions = Object.values(locSeen).sort(
      (a, b) => a.country.localeCompare(b.country) || a.label.localeCompare(b.label),
    )
    return [
      { field: 'created_at', headerName: 'Date', type: 'dateTime', sortable: true },
      { field: 'scan_type', headerName: 'Scan Type', type: 'statusChip', options: SCAN_TYPE_OPTIONS },
      // No `tampered` entry (VRG-491) — see the note above SCAN_TYPE_OPTIONS. This array is
      // the single source for BOTH the Filters drawer and the Sort menu, so dropping the
      // entry removes the axis from both at once; there is no second list to keep in step.
      { field: 'link_status', headerName: 'Link status', type: 'statusChip', options: LINK_STATUS_OPTIONS },
      { field: 'certificate_status', headerName: 'Certificate status', type: 'statusChip', options: certOptions },
      { field: 'location', headerName: 'Location', type: 'location', options: locationOptions },
    ]
  }, [scans])

  return (
    <Stack>
      <ActionLine
        showBulkActions={false}
        showSearch
        showSort
        showFilters
        showLayoutOptions={false}
        columns={columns}
        rows={scans}
        search={search}
        onSearchChange={setSearch}
        sort={sort}
        onSortChange={handleSortChange}
        filters={filters}
        onFiltersChange={setFilters}
      />

      {/* Scan list (left) + map (right) — the SAME `ScanMapPanel` the Analytics page's
          "Scans by location" card renders (§36/§47).

          The wrapper Box exists only to be MEASURED: `useFillBottom` reads its top to size
          the pane down to 24px above the viewport bottom (§22), and the panel then fills
          whatever number that produces. Held until the height is known so the map mounts
          ONCE at the right size instead of mounting at a fallback and re-tiling. */}
      <Box ref={bodyRef} sx={{ minHeight: 0 }}>
        {bodyHeight ? (
          <ScanMapPanel
            items={mapItems}
            activeId={activeId}
            onActiveIdChange={setActiveId}
            count={visibleScans.length}
            noun="scan"
            apiKey={GOOGLE_MAPS_API_KEY}
            height={bodyHeight}
            loading={showSkeleton}
            emptyMessage={`${emptyCopy.title}.`}
            /*
              ⚠️ ON now, and the fields that drive it stop being dead code (see `mapItems`).
              This map passed `enableClustering={false}` while computing `clusterPriority`
              and `clusterColor` for every pin — the Analytics map has always clustered, so
              a certificate scanned forty times in one city drew forty overlapping pins here
              and one labelled bubble there.
            */
            enableClustering
            /*
              VRG-451 — the ORDER this certificate moved between scans, oldest to newest.
              Cert-only, and it must stay that way: `MapProps.path` is explicit that a
              cross-subject set (an analytics map's scans, from many certificates) would
              draw a line implying a trip nobody took. One certificate is the one case where
              the points genuinely have a sequence.
            */
            path={showJourney ? journeyPath : undefined}
            /*
              VRG-491 — draw-to-filter. `drawMode` is what is ARMED, `drawnRing` is what is
              APPLIED, and the ring is rendered whether or not a tool is armed: a filter
              you cannot see the shape of is a filter that looks like missing data. The
              gesture's result lands in `handleDrawComplete`, which is where it is reduced,
              deduped against the current ring and committed.
            */
            drawMode={drawMode}
            drawnRing={drawnRing}
            onDrawComplete={handleDrawComplete}
            onDrawCancel={handleDrawCancel}
            // Excludes `drawnRing` on purpose — see the `fitSignature` memo above.
            fitSignature={fitSignature}
            canvasTopLeft={
              /*
                TWO chips want this corner now — the draw control and Journey — so the slot
                holds a ROW rather than a single control, and the row owns the coordinates.
                That is the one arrangement neither map had before: Analytics has a draw
                control and no Journey toggle, this tab had a Journey toggle and no draw
                control, so nothing had yet had to decide how they sit together.

                Side by side rather than stacked, because they are peers: both are
                map-level toggles, both are read at a glance, and a second row would push
                the lower chip into the tiles a reader is trying to look at. Left-to-right
                order puts DRAW first because it changes what the map contains, while
                Journey only changes how the same points are drawn — the more consequential
                control reads first.

                The draw control uses `variant="overlay"`, NOT `canvas`: `canvas` pins
                itself to top-left, which is exactly what a row cannot allow (both chips
                would land on the same 12,12). `overlay` keeps the shadow that separates a
                chip from map tiles and leaves the position to this Stack. Journey's own
                absolute positioning moved onto the Stack for the same reason.

                Top-left is the panel's caller slot; the pins/heat chip owns top-right and
                Google's own controls sit bottom-right.
              */
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ position: 'absolute', top: 12, left: 12, zIndex: 2 }}
              >
                <MapDrawControl
                  variant="overlay"
                  value={drawMode}
                  onChange={setDrawMode}
                  hasArea={Boolean(drawnRing)}
                  /*
                    Clearing drops the ring and nothing else — the search, the status chips
                    and the sort are untouched, so the view returns to whatever they alone
                    produced rather than to everything. §38: this is set-removal, which is
                    why the control is an `X` and takes no confirmation.
                  */
                  onClear={() => setDrawnRing(null)}
                />
                {/* Only when there is a real route to draw (≥2 distinct places). */}
                {canShowJourney ? (
                <Tooltip
                  title={
                    journeyPath.length < visibleScans.length
                      ? 'Connects scans in time order. Scans without a location are not shown, so the route may skip a stop.'
                      : 'Connects this certificate’s scans in time order.'
                  }
                  placement="right"
                >
                  {/*
                    The shared `Button`, NOT a hand-rolled `Box component="button"`
                    (§28/§47). This chip used to carry ~30 lines of bespoke `sx` — its own
                    border, its own `action.hover` fill, its own radius, its own 13px/500
                    font — which is how it ended up with a hover treatment that matched
                    nothing else on the page ("journey chip hover over is odd"). A primitive
                    with a shared component is a layer-3 file doing layer-1's job.

                    It is a TOGGLE, so the on/off states are carried by VARIANT:
                    `secondary` off, `primary` on. The shared `Button` has no dedicated
                    pressed treatment (no `selected`/`pressed` prop; the variants are
                    primary | secondary | tertiary | text), and `primary` is the right
                    borrowing rather than a workaround — it is the solid, filled state among
                    outlined secondaries, which is exactly the "engaged" reading a pressed
                    toggle wants, and it follows the theme in both modes where the old
                    hardcoded `brand[600]` + `common.white` did not.

                    `aria-pressed` survives the swap and is what actually carries the toggle
                    semantics to a screen reader — variant is the visual half only. It
                    reaches the DOM because `Button` spreads its remaining props onto
                    `MuiButton`.

                    No `size` — the default is 40 (§60), which is what makes this chip and
                    the render-mode chip on the same canvas the same height.
                  */}
                  <Button
                    variant={showJourney ? 'primary' : 'secondary'}
                    onClick={() => setShowJourney((v) => !v)}
                    aria-pressed={showJourney}
                    startIcon={<Route size={14} strokeWidth={1.5} />}
                    /*
                      No positioning of its own any more — the wrapping Stack owns the
                      corner now that two chips share it. Left here, `position: absolute`
                      would pin this chip to 12,12 and park it underneath the draw control.
                    */
                  >
                    Journey
                  </Button>
                </Tooltip>
                ) : null}
              </Stack>
            }
            list={
              <ScanList
                scans={visibleScans}
                isFirstPage
                activeId={activeId}
                onItemClick={(scanId) => setActiveId(scanId)}
                emptyTitle={emptyCopy.title}
                emptyText={emptyCopy.text}
              />
            }
          />
        ) : (
          // Hold the map area until the height is measured, so the panel mounts ONCE at the
          // right size instead of mounting at a fallback size then remounting (flash).
          <Box sx={{ width: '100%', height: 320 }} />
        )}
      </Box>

      {/* The "Learn more about scan states" link that used to sit here is gone (§44).
          It was a floating text button pinned bottom-right, detached from the thing it
          explained, and — because the pane above it now fills to the 24px gutter — it
          was pushed past the viewport bottom and could not be clicked at 1280x800 at
          all. Its content was six state definitions, which are terms of art, so they
          now live once in `docs/glossary.json` and are read from there by the chip's
          own `InfoTooltip` (see ScanItem), by the glossary page, and by search. That
          puts the definition ON the badge that raised the question instead of in a
          modal the reader has to go find. */}
    </Stack>
  )
}
